/* SSL cart offers — Agentcy for SickScience
   ssl-cart-offer: adds the set first (so nothing is removed if it fails), then lowers the replaced lines by one.
     Tracking: appends the step to the cart attribute "Cart set upgrade" and tags the set with the hidden line
     property _set_upgrade, which reaches the order only if the set is still in the cart at checkout.
   ssl-cart-subscribe: puts the chosen recurring selling plan on an existing one-time line.
     Tracking: opening the offer sets the cart attribute "Cart subscription offer" to "opened" (once per cart);
     switching sets it to "switched: <product> (<plan>)" and tags the line with the hidden property _sub_offer = cart_v2,
     which stays on the order only if the line is still a subscription at checkout.
   ssl-cart-pair: adds a paired product on the same recurring selling plan.
     Tracking: sets the cart attribute "Cart subscription pair" to "added: <product> (<plan>) with <anchor>" and tags
     the added line with _sub_offer = cart_v2_pair.
   The last cart request of each action asks for the cart drawer section (and the cart JSON) in the same response,
   so the drawer re-renders right away. Older fallback (cart.js + cart:refresh + reload) only runs if that is missing. */
(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_JSON = 'ssl-cart-json';
  const SUB_ATTR = 'Cart subscription offer';
  const SUB_PROP = '_sub_offer';
  const SUB_PROP_VALUE = 'cart_v2';
  const PAIR_ATTR = 'Cart subscription pair';
  const PAIR_PROP_VALUE = 'cart_v2_pair';
  const SET_ATTR = 'Cart set upgrade';
  const SET_PROP = '_set_upgrade';

  const post = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const onCartPage = () => /\/cart\/?$/.test(window.location.pathname);

  /* Section ids the theme wants re-rendered with a cart change (the cart drawer registers itself here). */
  const bundledSections = (withCartJson) => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    if (withCartJson) sections.push(CART_JSON);
    return [...new Set(sections)].slice(0, 5);
  };

  const withSections = (body, withCartJson) => {
    if (onCartPage()) return body;
    const sections = bundledSections(withCartJson);
    if (!sections.length) return body;
    return { ...body, sections: sections.join(','), sections_url: window.location.pathname };
  };

  const readCartJson = (sections) => {
    const html = sections && sections[CART_JSON];
    const match = html && html.match(/<script[^>]*data-ssl-cart-json[^>]*>([\s\S]*?)<\/script>/);
    if (!match) return null;
    try {
      const cart = JSON.parse(match[1]);
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      return null;
    }
  };

  const fire = (cart) => {
    ['cart:change', 'cart:updated'].forEach((name) => {
      document.documentElement.dispatchEvent(
        new CustomEvent(name, { bubbles: true, detail: { cart, baseEvent: 'variant:add' } })
      );
    });
  };

  /* json: response of the last cart call. Full cart for change/update, line items for add. */
  const refreshCart = async (el, json) => {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    const sections = json && json.sections;
    if (sections && Object.keys(sections).some((id) => id !== CART_JSON && sections[id])) {
      let cart = Array.isArray(json.items) && 'item_count' in json ? { ...json } : readCartJson(sections);
      if (!cart) cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const drawerSections = { ...sections };
      delete drawerSections[CART_JSON];
      cart.sections = drawerSections;
      fire(cart);
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    setTimeout(() => { if (el.isConnected) window.location.reload(); }, 2500);
  };

  const setBusy = (btn, busy) => {
    if (!btn) return;
    btn.disabled = busy;
    if (busy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  };

  if (!customElements.get('ssl-cart-offer')) {
    class SslCartOffer extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-offer-btn]');
        this.err = this.querySelector('[data-offer-error]');
        this.btn?.addEventListener('click', () => this.apply());
      }

      /* "upgraded: Shapeshift → Sculpted Duo", "swapped: …" or "upgraded for free shipping: …" */
      step() {
        const verb = { exact: 'swapped', ship: 'upgraded for free shipping', up: 'upgraded' }[this.dataset.kind] || 'upgraded';
        return `${verb}: ${this.dataset.from || 'items'} → ${this.dataset.title || 'set'}`;
      }

      async apply() {
        if (this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          let updates = {};
          try { updates = JSON.parse(this.dataset.updates || '{}'); } catch (e) { updates = {}; }
          const hasUpdates = Object.keys(updates).length > 0;
          const step = this.step();

          /* Tracking: every step is appended to the cart attribute (kept even if the customer later reverts);
             the hidden line property on the set only reaches the order if the set is still there at checkout.
             Failures are ignored so tracking never blocks the upgrade. */
          const log = [this.dataset.attr, step].filter(Boolean).join(' | ');
          await post('cart/update.js', { attributes: { [SET_ATTR]: log } }).catch(() => null);

          /* The set is added first (with its tracking property), then the replaced lines are lowered by line key,
             so nothing is removed if the add fails. */
          const addBody = { items: [{ id: Number(this.dataset.add), quantity: 1, properties: { [SET_PROP]: step } }] };
          const add = await post('cart/add.js', hasUpdates ? addBody : withSections(addBody, true));
          if (!add.ok) throw new Error(`cart/add ${add.status}`);
          let last = await add.json();
          if (hasUpdates) {
            const up = await post('cart/update.js', withSections({ updates }, false));
            if (!up.ok) throw new Error(`cart/update ${up.status}`);
            last = await up.json();
          }
          await refreshCart(this, last);
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-offer', SslCartOffer);
  }

  if (!customElements.get('ssl-cart-subscribe')) {
    class SslCartSubscribe extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-sub-btn]');
        this.err = this.querySelector('[data-sub-error]');
        this.box = this.querySelector('details');
        this.select = this.querySelector('select[name="ssl-sub-freq"]');
        /* Keep the dropdown's change/input events away from the drawer's own cart-line handlers. */
        ['change', 'input'].forEach((type) => this.select?.addEventListener(type, (e) => e.stopPropagation()));
        this.btn?.addEventListener('click', () => this.apply());
        /* Opening is logged once per cart; an already "switched" cart is never downgraded to "opened". */
        this.box?.addEventListener('toggle', () => {
          if (this.box.open && !this.dataset.attr) this.log('opened');
        });
      }

      /* Cart attribute write only: no drawer re-render, failures are ignored (tracking must never block the cart). */
      log(value) {
        this.dataset.attr = value;
        return post('cart/update.js', { attributes: { [SUB_ATTR]: value } }).catch(() => null);
      }

      lineProperties() {
        let props = {};
        try { props = JSON.parse(this.dataset.props || '{}'); } catch (e) { props = {}; }
        if (!props || Array.isArray(props) || typeof props !== 'object') props = {};
        return { ...props, [SUB_PROP]: SUB_PROP_VALUE };
      }

      async apply() {
        const picked = this.select ? this.select.selectedOptions[0] : null;
        if (!picked || !picked.value || this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          await this.log(`switched: ${this.dataset.title || 'item'} (${picked.dataset.name || picked.value})`);
          const res = await post('cart/change.js', withSections({
            id: this.dataset.key,
            quantity: Number(this.dataset.qty) || 1,
            selling_plan: Number(picked.value),
            properties: this.lineProperties(),
          }, false));
          if (!res.ok) throw new Error(`cart/change ${res.status}`);
          await refreshCart(this, await res.json());
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-subscribe', SslCartSubscribe);
  }

  if (!customElements.get('ssl-cart-pair')) {
    class SslCartPair extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-pair-btn]');
        this.err = this.querySelector('[data-pair-error]');
        this.btn?.addEventListener('click', () => this.apply());
      }

      async apply() {
        if (this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          /* Tracking only: failures are ignored so they never block the add. */
          const note = `added: ${this.dataset.title || 'item'} (${this.dataset.planName || 'plan'}) with ${this.dataset.anchor || 'subscription'}`;
          await post('cart/update.js', { attributes: { [PAIR_ATTR]: note } }).catch(() => null);
          const res = await post('cart/add.js', withSections({
            items: [{
              id: Number(this.dataset.variant),
              quantity: 1,
              selling_plan: Number(this.dataset.plan),
              properties: { [SUB_PROP]: PAIR_PROP_VALUE },
            }],
          }, true));
          if (!res.ok) throw new Error(`cart/add ${res.status}`);
          await refreshCart(this, await res.json());
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-pair', SslCartPair);
  }
})();
